import React, { useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { ArrowBack, Info, Science, Send, Warning } from '@mui/icons-material';
import { blueGrey, grey, orange } from '@mui/material/colors';
import { Avatar, Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DealActivityLinkType, SmsDirection } from '@trnsact/trnsact-shared-types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { InputField } from 'components/form/rhf/InputField';

import { SendMessageForm, SmsConversationData } from '../../types';
import { dealActivityApi } from '../../api';
import {
  CreateSmsActivityQuery,
  CreateSmsActivityVariables,
  MarkSmsActivitiesReadQuery,
  MarkSmsActivitiesReadVariables,
  SmsConversationQuery,
  SmsConversationVariables,
} from '../../api/types';
import { ConversationMessageBubble } from './ConversationMessageBubble';
import { useSelector } from 'react-redux';
import { useSmsActivityReceived } from 'hooks/useSubscriptions';

interface Props {
  data: SmsConversationData;
  parentType: DealActivityLinkType;
  onBack: VoidFunction;
}

export const SmsConversationCard = ({ data, parentType, onBack }: Props) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentUserProfile = useSelector((state: any) => state.userProfile);

  const { control, handleSubmit, reset, watch } = useForm<SendMessageForm>({
    defaultValues: { message: '' },
  });

  const messageValue = watch('message');
  const hasConversation = data.conversationSequenceNumber !== null;

  const {
    data: response,
    loading,
    refetch,
  } = useQuery<SmsConversationQuery, SmsConversationVariables>(dealActivityApi.smsConversation, {
    skip: !hasConversation,
    variables: {
      input: {
        vendorOpportunityId: data.voId,
        conversationSequenceNumber: data.conversationSequenceNumber!,
      },
    },
  });

  const [createSmsActivity, { loading: sending }] = useMutation<CreateSmsActivityQuery, CreateSmsActivityVariables>(
    dealActivityApi.createSmsActivity
  );

  const [markSmsActivitiesRead] = useMutation<MarkSmsActivitiesReadQuery, MarkSmsActivitiesReadVariables>(
    dealActivityApi.markSmsActivitiesRead
  );

  const { data: messageEvent } = useSmsActivityReceived(data.voId);

  const messages = response?.messaging?.smsActivities?.list ?? [];
  const existingConversationSid =
    messages.find(m => m.twilioConversationSid != null)?.twilioConversationSid ?? undefined;

  useEffect(() => {
    if (hasConversation) {
      markSmsActivitiesRead({
        variables: {
          vendorOpportunityId: data.voId,
          conversationSequenceNumber: data.conversationSequenceNumber!,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (messageEvent?.smsActivityReceived && hasConversation) {
      refetch();
    }
  }, [messageEvent]);

  const onSubmit = async ({ message }: SendMessageForm) => {
    await createSmsActivity({
      variables: {
        input: {
          parentId: data.voId,
          parentType,
          ownerPgCreditApplicationId: data.contactId,
          message: message.trim(),
          smsDirection: SmsDirection.Outgoing,
          conversationSequenceNumber: data.conversationSequenceNumber ?? 0,
          recipientPhone: '+1' + (data.contactPhone?.replace(/\D/g, '') ?? ''),
          senderIdentity: currentUserProfile.email,
          twilioConversationSid: existingConversationSid,
          // isAgent is never true for dealer-typed messages — data.isAgent means the
          // conversation belongs to the AI agent, not that this message is from it.
          isAgent: false,
          demoMode: data.demoMode,
        },
      },
    });

    reset();

    if (hasConversation) {
      refetch();
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  return (
    <Box className={classes.root}>
      <Stack direction="row" alignItems="center" gap="0.5rem" sx={{ padding: '0.75rem 0' }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBack />
        </IconButton>

        <Typography variant="h6" fontWeight="bold">
          Message Details
        </Typography>
      </Stack>

      <Paper sx={{ display: 'flex', flexDirection: 'column', height: `calc(100% - 3rem)` }}>
        {data.demoMode && (
          <Stack direction="row" alignItems="center" gap="1rem" sx={{ px: 3, py: 1, bgcolor: '#1a1a1a' }}>
            <Warning sx={{ fontSize: 16, color: orange[400] }} />
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ color: orange[400], letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              Demo Mode Active
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.500' }}>
              · &nbsp;Simulated environment — no real customers affected
            </Typography>
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="center"
          gap="0.75rem"
          sx={{ padding: '12px 24px', bgcolor: data.demoMode ? orange[50] : blueGrey[50] }}
        >
          <Avatar className={classes.contactAvatar}>
            <Typography fontWeight={500}>{data.contactInitials}</Typography>
          </Avatar>

          <Stack sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" gap="0.5rem">
              <Typography variant="subtitle1" fontWeight="bold">
                {data.contactName}
              </Typography>

              {data.demoMode && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    borderRadius: '6px',
                    padding: '2px 6px',
                    color: orange[800],
                    backgroundColor: orange[100],
                  }}
                >
                  <Science sx={{ fontSize: 12 }} />
                  <Typography component="span" variant="subtitle2" sx={{ fontSize: '12px' }}>
                    Demo
                  </Typography>
                </Box>
              )}
            </Stack>

            <Typography variant="caption" color="textSecondary">
              {data.contactPhone}
              {data.contactTitle && ` • ${data.contactTitle}`}
            </Typography>
          </Stack>
        </Stack>

        <Box className={classes.messagesContainer}>
          <RenderWithFallback
            isLoading={loading}
            fallBack={
              <Stack height="100%" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Stack>
            }
          >
            <Stack gap="1rem" sx={{ padding: '1rem 0' }}>
              {messages.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ padding: '2rem 0' }}>
                  <Typography variant="subtitle2" component="span">
                    No messages yet
                  </Typography>
                </Stack>
              ) : (
                messages.map(message => <ConversationMessageBubble key={message.id} message={message} />)
              )}
              <Box ref={messagesEndRef} />
            </Stack>
          </RenderWithFallback>
        </Box>

        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          gap="0.75rem"
          sx={{ padding: '16px 24px', borderTop: `1px solid ${grey[300]}` }}
        >
          {data.demoMode && (
            <Stack
              direction="row"
              alignItems="center"
              gap="0.5rem"
              sx={{
                bgcolor: orange[50],
                border: `1px solid ${orange[200]}`,
                borderRadius: '6px',
                padding: '8px 12px',
                color: orange[800],
              }}
            >
              <Info sx={{ fontSize: 16, flexShrink: 0 }} />
              <Typography variant="body2">
                You are typing as <strong>{data.contactName}</strong> (test applicant). The agent will reply
                automatically.
              </Typography>
            </Stack>
          )}

          <InputField<SendMessageForm>
            name="message"
            control={control}
            inputProps={{
              multiline: true,
              minRows: 4,
              maxRows: 4,
              placeholder: data.demoMode ? `Type as ${data.contactName}...` : 'Text Message',
              variant: 'outlined',
              fullWidth: true,
            }}
          />

          <Box>
            <Button
              type="submit"
              variant="outlined"
              color={data.demoMode ? 'warning' : 'primary'}
              disabled={!messageValue.trim() || sending}
              startIcon={data.demoMode ? <Send /> : undefined}
            >
              {sending ? 'Sending...' : data.demoMode ? 'Send' : 'Send Message'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  contactAvatar: {
    width: 40,
    height: 40,
    backgroundColor: '#90caf9',
    color: 'black',
    fontSize: '14px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: '200px',
    backgroundColor: '#F7F9F9',
    padding: '0 24px 24px 24px',
  },
});
